import { css } from "@emotion/css";
import { Button, Callout, ExternalLink } from "@octopusdeploy/design-system-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDGatewayHealthStatus, ArgoCDInstanceSummaryResource, EnvironmentResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { ReactNode } from "react";
import React, { useState, useCallback } from "react";
import { AddArgoCDInstanceDialog } from "~/areas/infrastructure/components/ArgoCDInstances/AddArgoCDInstanceDialog";
import type { ArgoCDInstanceConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceConfiguration";
import { projectStatusActions } from "~/areas/projects/components/ProjectStatus/actions";
import type { ProjectStatus } from "~/areas/projects/components/ProjectStatus/useProjectStatus";
import { repository } from "~/clientInstance";
import { RegisterArgoCDInstanceCard, RegisteredArgoCDInstanceCard } from "~/components/ProjectBasedActivation/RegisterArgoCDInstanceCard";
import { useRefreshLoop } from "~/hooks/useRefreshLoop";
import { GuidedSetupFrame } from "../OnboardingDialog/GuidedSetupFrame";
import { PermissionCheck } from "../PermissionCheck";
import ArgoCDInstanceHelpImage from "./assets/img-argo-cd-instance.svg";
type PartialInstanceModel = Pick<ArgoCDInstanceSummaryResource, "Name" | "EnvironmentIds" | "HealthStatus">;
interface ConnectArgoCDInstanceProps {
    spaceId: string;
    projectId?: string;
    correlationId?: string;
    title: ReactNode;
    hideBackButton?: boolean;
    isOnboarding?: boolean;
    next: () => void;
    skip: () => void;
    close: () => void;
    onArgoCDInstancesAdded: (argoCDInstances: ArgoCDInstanceSummaryResource[]) => void;
    environments?: EnvironmentResource[];
    projectStatus: ProjectStatus;
}
function ConnectArgoCDInstance(props: ConnectArgoCDInstanceProps) {
    const [isConnectingGateway, setIsConnectingGateway] = useState<boolean>(false);
    const [bearerTokenExpired, setBearerTokenExpired] = React.useState(false);
    const [nameOfNewInstance, setNameOfNewInstance] = useState<string | undefined>();
    const [addedArgoCDInstances, setAddedArgoCDInstances] = useState<ArgoCDInstanceSummaryResource[]>([]);
    const [partialInstanceModel, setPartialInstanceModel] = useState<PartialInstanceModel>();
    async function refreshTargets() {
        if (nameOfNewInstance) {
            const instanceSummaries = await repository.ArgoCDInstances.list({ name: nameOfNewInstance });
            //the filtering should mean we only have one
            const newInstance = instanceSummaries.Resources[0];
            if (newInstance) {
                let updatedInstances = [...addedArgoCDInstances];
                const targetToUpdateIndex = updatedInstances.findIndex((x) => x.Id === newInstance.Id);
                if (targetToUpdateIndex >= 0) {
                    updatedInstances[targetToUpdateIndex] = newInstance;
                }
                else {
                    updatedInstances = [...updatedInstances, newInstance];
                }
                setAddedArgoCDInstances(updatedInstances);
                props.onArgoCDInstancesAdded(updatedInstances);
                //we now have an actual model (the instance has registered, so we can use this one now
                setPartialInstanceModel(undefined);
            }
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedRefreshFunction = useCallback(refreshTargets, [partialInstanceModel, addedArgoCDInstances]);
    useRefreshLoop(memoizedRefreshFunction, 2000);
    const onNextClicked = () => {
        props.next();
    };
    const hasInstanceWithHealthStatus = (healthStatus: ArgoCDGatewayHealthStatus): boolean => addedArgoCDInstances.some((instance) => instance.HealthStatus === healthStatus);
    const handleInstanceConfigurationChanged = (configuration: ArgoCDInstanceConfiguration) => {
        setNameOfNewInstance(configuration.name);
        setPartialInstanceModel({
            Name: configuration.name,
            EnvironmentIds: configuration.environmentIds,
            HealthStatus: "Unknown",
        });
    };
    return (<GuidedSetupFrame helpPanelContent={<div>
                    <p>
                        Register your <strong>Argo CD instances</strong> in Octopus to combine powerful CD capabilities like environment promotion with GitOps workflows.
                    </p>

                    <p>Once you've installed the gateway into your cluster, you can use our built-in steps to automatically update Argo CD Application container image tags or manifests.</p>
                </div>} helpPanelImage={{ src: ArgoCDInstanceHelpImage, altText: "Argo CD in Octopus" }} showThemeSelector={props.isOnboarding} onClose={props.close}>
            <>
                <div className={styles.title}>{props.title}</div>
                <div className={styles.heading}>{hasInstanceWithHealthStatus("Healthy") ? "Argo CD instance registered on cluster" : "Connect Octopus to your Argo CD instance"}</div>
                <PermissionCheck permission={Permission.MachineCreate} alternate={<Callout type="danger" title="Can't add Argo CD instances">
                            You don’t have permission to register Argo CD instances. Please contact your Octopus administrator to request {Permission.MachineCreate} permission. You can proceed to the next step and register Argo CD instances, when you
                            have permission.
                        </Callout>}>
                    <div>
                        {!isConnectingGateway && addedArgoCDInstances.length === 0 && !partialInstanceModel && <RegisterArgoCDInstanceCard onClick={() => setIsConnectingGateway(true)}/>}
                        <AddArgoCDInstanceDialog isGuidedSetup open={isConnectingGateway} onClose={() => setIsConnectingGateway(false)} spaceId={props.spaceId} showProjectSelection={false} onInstanceConfigurationChanged={handleInstanceConfigurationChanged} onBearerTokenExpired={() => setBearerTokenExpired(true)}/>
                    </div>

                    <div className={styles.kubernetesAgentCardsWrapper}>
                        {addedArgoCDInstances.map((instance) => {
            const environmentNames = props.environments?.filter((env) => instance.EnvironmentIds.includes(env.Id)).map((env) => env.Name);
            return (<div key={instance.Name}>
                                    <RegisteredArgoCDInstanceCard key={instance.Id} name={instance.Name} id={instance.Id} spaceId={props.spaceId} environments={environmentNames} healthStatus={instance.HealthStatus} close={props.close} onInstanceAdded={() => props.projectStatus.dispatch({ type: projectStatusActions.argoCDInstanceAdded })}/>
                                </div>);
        })}
                        {partialInstanceModel && (<div>
                                <RegisteredArgoCDInstanceCard key={partialInstanceModel.Name} name={partialInstanceModel.Name} id={partialInstanceModel.Name} spaceId={props.spaceId} environments={props.environments?.filter((env) => partialInstanceModel.EnvironmentIds.includes(env.Id)).map((env) => env.Name)} healthStatus={partialInstanceModel.HealthStatus} close={props.close} forceNotFound onInstanceAdded={() => props.projectStatus.dispatch({ type: projectStatusActions.argoCDInstanceAdded })} bearerTokenExpired={bearerTokenExpired}/>
                            </div>)}
                    </div>

                    {(addedArgoCDInstances.length > 0 || bearerTokenExpired) && (<div>
                            <Button importance="secondary" label="Add Another Argo CD Instance" onClick={() => {
                setNameOfNewInstance(undefined);
                setPartialInstanceModel(undefined);
                setIsConnectingGateway(true);
                setBearerTokenExpired(false);
            }}/>
                        </div>)}

                    {hasInstanceWithHealthStatus("Unknown") && (<div className={styles.healthCheckCallout}>
                            <Callout canClose={false} hideTitle type="information">
                                <span>Registration and health check can take up to 3 minutes. You can stay here or proceed – we’ll notify you if an error occurs.</span>
                            </Callout>
                        </div>)}

                    {addedArgoCDInstances
            .filter((instance) => instance.HealthStatus === "Unavailable" || instance.HealthStatus === "ArgoCDConnectionIssues")
            .map((instance) => (<div key={instance.Id} className={styles.healthCheckCallout}>
                                <Callout canClose={false} hideTitle type="danger">
                                    <span>
                                        Health check failed for <b>{instance.Name}</b>. Need help? <ExternalLink href={"argo-cd-octopus-gateway-installation-docs"}>Read the docs</ExternalLink>
                                    </span>
                                </Callout>
                            </div>))}
                </PermissionCheck>

                <div className={styles.buttons}>
                    <Button label="I'll do this later" importance="quiet" onClick={props.skip}/>
                    {!props.hideBackButton && <Button label="Back" disabled={true} importance="secondary" onClick={() => { }}/>}
                    <Button label="Next" disabled={addedArgoCDInstances.length === 0} onClick={onNextClicked} importance="primary"/>
                </div>
            </>
        </GuidedSetupFrame>);
}
export default ConnectArgoCDInstance;
const styles = {
    title: css({
        marginTop: space[40],
        marginBottom: space[16],
    }),
    heading: css({
        marginTop: space[32],
        marginBottom: space[32],
        font: text.heading.large,
    }),
    buttons: css({
        display: "flex",
        justifyContent: "flex-end",
        gap: space[8],
        paddingTop: space[48],
        marginTop: "auto",
        marginBottom: 0,
    }),
    kubernetesAgentCardsWrapper: css({
        display: "flex",
        flexWrap: "wrap",
        gap: space[16],
        marginBottom: space[16],
    }),
    healthCheckCallout: css({
        marginTop: space[32],
    }),
};
